/**
 * GamePage — the orchestrator. Owns the XState actor (via useGame), reads its state, and renders the
 * right screen: the briefing, a scenario (with the pop-up interrupt overlaid when one fires), or the
 * end-screen debrief. A mid-run snapshot rehydrates straight into play, so a refresh resumes exactly.
 * Server-side autosave (useServerAutosave) mirrors the run to the backend; a small nav sits on the
 * briefing/end screens; and in untimed practice the player can exit mid-game (save or discard).
 */
import { useEffect, useState } from "react";
import { useSelector } from "@xstate/react";
import { Link, useNavigate } from "react-router-dom";
import { gameContent, interruptsByHost, playSequence } from "../content";
import { useGame } from "../game/useGame";
import { useServerAutosave } from "../game/useServerAutosave";
import { useSession } from "../store/session";
import { useLogout } from "../api/auth";
import { deleteRun } from "../api/runs";
import { useAuth } from "./AuthProvider";
import { Brief } from "../components/Brief";
import { Hud } from "../components/Hud";
import { ScenarioView } from "../components/ScenarioView";
import { PopupDialog } from "../components/PopupDialog";
import { EndScreen } from "../components/EndScreen";
import { BriefingPanel } from "../components/Briefing";
import { useFocusTrap } from "../lib/useFocusTrap";

const LIMIT_MS = gameContent.meta.timeLimitSeconds * 1000;

function GameNav() {
  const { user } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  return (
    <nav className="absolute right-3 top-3 z-30 flex max-w-[62vw] flex-wrap items-center justify-end gap-x-2 gap-y-1 text-sm sm:right-6 sm:top-4 sm:max-w-none">
      <Link to="/history" className="text-ink-600 hover:text-ink">History</Link>
      <span className="text-ink-300" aria-hidden="true">·</span>
      <Link to="/account" className="text-ink-600 hover:text-ink">Account</Link>
      {user?.kind === "admin" && (
        <>
          <span className="text-ink-300" aria-hidden="true">·</span>
          <Link to="/admin" className="text-ink-600 hover:text-ink">Admin</Link>
        </>
      )}
      <span className="text-ink-300" aria-hidden="true">·</span>
      <button
        onClick={async () => {
          await logout.mutateAsync();
          navigate("/", { replace: true });
        }}
        className="text-ink-600 hover:text-brand"
      >
        Sign out
      </button>
    </nav>
  );
}

/** Practice-mode exit confirmation (untimed only). */
function ExitDialog({
  onSave,
  onDiscard,
  onCancel,
}: {
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  const ref = useFocusTrap<HTMLDivElement>(onCancel);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4 outline-none backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-md border border-ink-200 bg-card p-6 shadow-[var(--shadow-lg)]">
        <h2 id="exit-title" className="font-display text-fluid-lg font-semibold text-ink">Leave this game?</h2>
        <p className="mt-2 text-sm leading-relaxed text-ink-600">Choose how to leave this game:</p>
        <div className="mt-5 flex flex-col gap-3">
          <button onClick={onSave} className="rounded-sm border border-ink-200 p-3 text-left transition-colors hover:border-ink-400">
            <span className="font-medium text-ink">Save &amp; exit</span>
            <span className="mt-0.5 block text-sm text-ink-600">
              Your progress is kept. You'll resume this game where you left off next time you press Play,
              and it stays in your history as "in progress".
            </span>
          </button>
          <button onClick={onDiscard} className="rounded-sm border border-ink-200 p-3 text-left transition-colors hover:border-[var(--danger)]">
            <span className="font-medium text-ink">Exit without saving</span>
            <span className="mt-0.5 block text-sm text-ink-600">
              This game is discarded and removed from your history. You'll start fresh from the briefing.
            </span>
          </button>
        </div>
        <div className="mt-5 flex justify-end">
          <button onClick={onCancel} className="rounded-sm px-4 py-2 text-sm text-ink-600 hover:text-ink">Keep playing</button>
        </div>
      </div>
    </div>
  );
}

export function GamePage() {
  const actor = useGame();
  const state = useSelector(actor, (s) => s);
  const ctx = state.context;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showExit, setShowExit] = useState(false);
  const [showBackground, setShowBackground] = useState(false);
  useServerAutosave(actor);

  // On each screen change — a new question (index changes), the end-screen debrief, or back to the
  // briefing — scroll to the top so the player starts reading from the beginning rather than wherever
  // they'd scrolled to on the previous screen. Honours prefers-reduced-motion.
  const viewKey = state.matches("finished")
    ? "finished"
    : state.matches("briefing")
      ? "briefing"
      : `scenario-${ctx.index}`;
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  }, [viewKey]);

  // Players may exit untimed practice only; admins may exit any run (timed or untimed).
  const canExit = ctx.mode === "untimed" || user?.kind === "admin";

  if (state.matches("briefing")) {
    return (
      <div className="relative min-h-svh">
        <GameNav />
        <Brief
          dimensions={gameContent.meta.dimensions}
          cutoffLabel={gameContent.meta.cutoffLabel}
          timeLimitMinutes={Math.round(gameContent.meta.timeLimitSeconds / 60)}
          onStart={(mode) => {
            useSession.getState().startRun();
            actor.send({ type: "START", mode, seed: Math.floor(Math.random() * 0x7fffffff) });
          }}
        />
      </div>
    );
  }

  if (state.matches("finished")) {
    const ending = gameContent.endings.find((e) => e.id === ctx.endingId) ?? null;
    return (
      <div className="relative min-h-svh">
        <GameNav />
        <EndScreen
          ending={ending}
          dimensions={gameContent.meta.dimensions}
          scores={ctx.scores}
          transcript={ctx.transcript}
          onReplay={() => {
            useSession.getState().clearRun();
            actor.send({ type: "RESTART" });
          }}
        />
      </div>
    );
  }

  // playing
  const scenario = playSequence[ctx.index];
  if (!scenario || scenario.kind === "popup") return null;
  // Keep the host shown as "revealed" while an after-answer pop-up is open over it.
  const triaging = state.matches({ playing: "triaging" }) || state.matches({ playing: "triagingAfter" });
  const revealing = state.matches({ playing: "revealing" }) || state.matches({ playing: "triagingAfter" });
  const interruptPopup = interruptsByHost.get(scenario.id);

  // "Save & exit" keeps the run (resumes on return); "Exit without saving" discards it.
  const saveAndExit = () => {
    setShowExit(false);
    navigate("/history");
  };
  const discardAndExit = () => {
    const runId = useSession.getState().runId;
    useSession.getState().clearRun();
    if (runId) void deleteRun(runId).catch(() => undefined);
    actor.send({ type: "RESTART" });
    setShowExit(false);
  };

  // While any modal overlays the game, make the SCENARIO inert: nothing behind the modal is focusable
  // or exposed to assistive tech (the after-answer pop-up in particular leaves the host scenario — incl.
  // its Continue button — rendered underneath). The Hud is deliberately left interactive: it holds the
  // Background/Exit triggers, and inerting a focused trigger would blur it before the focus-trap captures
  // it (breaking focus restoration). The modals' focus traps keep Tab contained regardless.
  const anyModal = triaging || showBackground || showExit;

  return (
    <div className="min-h-svh pb-16">
      <Hud
        phase={scenario.phase}
        current={ctx.index + 1}
        total={playSequence.length}
        elapsedMs={ctx.elapsedMs}
        limitMs={LIMIT_MS}
        mode={ctx.mode}
        dimensions={gameContent.meta.dimensions}
        scores={ctx.scores}
        onShowBackground={() => setShowBackground(true)}
        onExit={canExit ? () => setShowExit(true) : undefined}
      />
      <div inert={anyModal || undefined}>
        <ScenarioView
          scenario={scenario}
          seed={ctx.seed}
          revealing={revealing}
          revealOthers={ctx.mode === "untimed"}
          selectedChoiceId={ctx.selectedChoiceId}
          onSelect={(id) => actor.send({ type: "SELECT", choiceId: id })}
          onCommit={() => actor.send({ type: "COMMIT" })}
          onContinue={() => actor.send({ type: "CONTINUE" })}
          isLast={ctx.index === playSequence.length - 1}
        />
      </div>
      {triaging && interruptPopup && (
        <PopupDialog
          popup={interruptPopup}
          seed={ctx.seed}
          onSubmit={(ranking) => actor.send({ type: "SUBMIT_RANKING", ranking })}
        />
      )}
      {showBackground && <BriefingPanel onClose={() => setShowBackground(false)} />}
      {showExit && <ExitDialog onSave={saveAndExit} onDiscard={discardAndExit} onCancel={() => setShowExit(false)} />}
    </div>
  );
}
